/** Age-rating indicator colors (aligned with mobile). */
export const AGE_RATING_COLORS = {
  green: '#66FF00',
  yellow: '#FFEA00',
  orange: '#FF7B00',
  red: '#FF0000',
  fallback: '#ffffff',
} as const;

const MPAA_AGE_MAP: Record<string, number> = {
  G: 0,
  PG: 7,
  'PG-13': 13,
  R: 18,
  'NC-17': 18,
};

/** Extract numeric age from values like `7+`, `13+`, `PG-13`, `R`. */
export function parseAgeFromRating(ageRating?: string | null): number | null {
  if (!ageRating) return null;

  const normalized = ageRating.trim().toUpperCase();
  const mpaaAge = MPAA_AGE_MAP[normalized];
  if (mpaaAge !== undefined) return mpaaAge;

  const digits = ageRating.replace(/\D/g, '');
  if (!digits) return null;

  const age = Number.parseInt(digits, 10);
  return Number.isNaN(age) ? null : age;
}

/** 0+/7+ → green, 13+ → yellow, 16+ → orange, 18+ → red. */
export function getAgeRatingColor(ageRating?: string | null): string {
  const age = parseAgeFromRating(ageRating);
  if (age === null) return AGE_RATING_COLORS.fallback;
  if (age <= 7) return AGE_RATING_COLORS.green;
  if (age <= 13) return AGE_RATING_COLORS.yellow;
  if (age <= 16) return AGE_RATING_COLORS.orange;
  return AGE_RATING_COLORS.red;
}

/** Whether `rating` is an audience age label (not e.g. CC-BY). */
export function isLikelyAgeRating(rating: string): boolean {
  if (/^CC[-_]/i.test(rating.trim())) return false;
  if (parseAgeFromRating(rating) !== null) return true;
  return rating.trim().toUpperCase() in MPAA_AGE_MAP;
}

export function resolveMediaAgeRatingLabel(media: { agerating?: string; rating?: string }): string | undefined {
  if (media.agerating) return media.agerating;
  if (media.rating && isLikelyAgeRating(media.rating)) return media.rating;
  return undefined;
}
